import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Star, Loader2, CheckCircle2 } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { MENTOR_FEEDBACK } from '@/constants/testIds';

function StarRating({ value, onChange }) {
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange(n)}
                    className={`rounded-md p-1 transition-colors ${
                        n <= value ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500/60'
                    }`}
                    aria-label={`Rate ${n}`}
                >
                    <Star className={`h-6 w-6 ${n <= value ? 'fill-current' : ''}`} />
                </button>
            ))}
        </div>
    );
}

export default function MentorFeedbackPage() {
    const { bookingId } = useParams();
    const [ctx, setCtx] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/mentor/feedback/${bookingId}`);
                setCtx(data);
                if (data.already_submitted) setSubmitted(true);
            } catch (e) {
                setCtx(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [bookingId]);

    const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

    const submit = async (e) => {
        e.preventDefault();
        // Validate required fields
        const missing = (ctx?.form?.fields || []).filter((f) => f.required && (answers[f.id] === undefined || answers[f.id] === '' || answers[f.id] === null));
        if (missing.length > 0) {
            toast.error(`Please fill: ${missing.map((m) => m.label).join(', ')}`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/mentor/feedback/${bookingId}`, { answers });
            setSubmitted(true);
            toast.success('Thanks! Feedback submitted.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Submit failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!ctx) {
        return (
            <div className="flex min-h-screen items-center justify-center px-4">
                <Card className="max-w-md">
                    <CardContent className="p-8 text-center">
                        <h1 className="font-display text-xl font-semibold">Booking not found</h1>
                        <p className="mt-2 text-sm text-muted-foreground">This feedback link is invalid or expired.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-hero-soft px-4">
                <Card className="max-w-md text-center">
                    <CardContent className="p-8">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <CheckCircle2 className="h-7 w-7" />
                        </div>
                        <h1 className="font-display text-xl font-semibold">Thanks for submitting your feedback</h1>
                        <p className="mt-2 text-sm text-muted-foreground">Your notes have been shared with the admin.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { booking, form } = ctx;

    return (
        <div className="min-h-screen bg-hero-soft bg-noise">
            <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
                <div className="mb-6 text-sm text-muted-foreground">Session feedback</div>
                <Card>
                    <CardHeader>
                        <CardTitle className="font-display text-2xl">{form.title}</CardTitle>
                        <p className="text-sm text-muted-foreground">{form.description}</p>
                        <div className="mt-3 rounded-lg border border-border/60 bg-accent/30 p-3 text-sm">
                            <div><strong>Student:</strong> {booking.student_name} <span className="text-muted-foreground">({booking.student_email})</span></div>
                            <div><strong>Session:</strong> {formatDateTime(booking.start_iso)} · {booking.duration_minutes} min</div>
                            {booking.topic && <div><strong>Topic:</strong> {booking.topic}</div>}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={submit} className="space-y-6">
                            {form.fields.map((f) => (
                                <div key={f.id}>
                                    <Label className="mb-1 block text-sm font-medium">
                                        {f.label} {f.required && <span className="text-destructive">*</span>}
                                    </Label>
                                    {f.help_text && <p className="mb-2 text-xs text-muted-foreground">{f.help_text}</p>}
                                    {f.type === 'text' && (
                                        <Input value={answers[f.id] || ''} onChange={(e) => setAnswer(f.id, e.target.value)} />
                                    )}
                                    {f.type === 'textarea' && (
                                        <Textarea rows={3} value={answers[f.id] || ''} onChange={(e) => setAnswer(f.id, e.target.value)} />
                                    )}
                                    {f.type === 'number' && (
                                        <Input type="number" value={answers[f.id] || ''} onChange={(e) => setAnswer(f.id, e.target.value)} />
                                    )}
                                    {f.type === 'rating' && (
                                        <StarRating value={Number(answers[f.id]) || 0} onChange={(v) => setAnswer(f.id, v)} />
                                    )}
                                    {f.type === 'yesno' && (
                                        <RadioGroup value={answers[f.id] || ''} onValueChange={(v) => setAnswer(f.id, v)}>
                                            {['yes', 'no'].map((opt) => (
                                                <label key={opt} className="flex items-center gap-2 text-sm capitalize">
                                                    <RadioGroupItem value={opt} id={`${f.id}-${opt}`} />
                                                    {opt}
                                                </label>
                                            ))}
                                        </RadioGroup>
                                    )}
                                    {f.type === 'multichoice' && (
                                        <RadioGroup value={answers[f.id] || ''} onValueChange={(v) => setAnswer(f.id, v)}>
                                            {(f.options || []).map((opt) => (
                                                <label key={opt} className="flex items-center gap-2 text-sm">
                                                    <RadioGroupItem value={opt} id={`${f.id}-${opt}`} />
                                                    {opt}
                                                </label>
                                            ))}
                                        </RadioGroup>
                                    )}
                                </div>
                            ))}
                            <Button type="submit" className="w-full" data-testid={MENTOR_FEEDBACK.submit} disabled={submitting}>
                                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Submit feedback
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
